"use client";

import { Fragment, RefObject, useEffect, useRef, useState } from "react";
import { MdLocationPin } from "react-icons/md";
import usePlacesAutocomplete, { getDetails } from "use-places-autocomplete";
import { useLocalStorage, useOnClickOutside } from "usehooks-ts";
import country from "country-list-js";

import useToggleState from "src/hooks/use-toggle-state";
import { useGoogleMapsContext } from "src/lib/contexts/google-map-context";
import { Input } from "src/components/Inputs/Input";

/**
 * What the input lets the user pick.
 *  address - any street address (company profile). Today's behaviour.
 *  city    - city-level places only; what job-location matching compares.
 *  country - countries only, for a remote job's country scope. Google has no
 *            country-only type, so we ask for regions and keep the countries.
 */
export type PlaceKind = "address" | "city" | "country";

type Props = {
  id: string;
  placeholder?: string;
  kind?: PlaceKind;
  /** Fires with the picked place. */
  selectHandler?: (selected: google.maps.places.PlaceResult) => void;
  /**
   * Fires when the user edits the text after picking - typed text is never a
   * location, so the parent should clear its value and let `required` catch
   * an unpicked field.
   */
  onClear?: () => void;
  /**
   * ISO 3166-1 alpha-2 to limit suggestions to. Defaults: the visitor's
   * detected country for `address`, Nigeria for `city`, none for `country`.
   */
  country?: string | null;
  /** Text to show initially (e.g. the saved value when editing). */
  defaultText?: string;
};

/** What the API's userLocation payload expects; mirrors LocationSystem.UpdateLocation. */
export type PlaceLocation = {
  city: string;
  state: string;
  country: string;
  country_code: string;
  region: string;
  continent: string;
  currency: {
    curency_code?: string;
    curency_symbol?: string;
    dialing_code?: string;
    country_iso_2?: string;
    country_iso_3?: string;
  };
  /** "City, State, Country" for display. */
  label: string;
};

export type PlaceCountry = { country: string; country_code: string };

const component = (
  components: google.maps.GeocoderAddressComponent[],
  type: string
) => components.find((c) => c.types.includes(type));

/**
 * Break a `(cities)` place into the fields matching compares.
 *
 * Google only guarantees a "locality" for places it considers cities. Towns
 * inside a larger municipality come back as postal_town (UK), or as an
 * administrative_area_level_2/3 with no locality at all (common in Nigeria,
 * e.g. LGAs). So walk down the chain until something is named.
 *
 * Returns null when there is no usable city or country, so a caller can treat
 * it exactly like "nothing picked".
 */
export const extractPlaceLocation = (
  place: google.maps.places.PlaceResult | null | undefined
): PlaceLocation | null => {
  const components = place?.address_components;
  if (!components?.length) return null;

  const countryPart = component(components, "country");
  const cityPart =
    component(components, "locality") ??
    component(components, "postal_town") ??
    component(components, "administrative_area_level_3") ??
    component(components, "administrative_area_level_2");
  const statePart = component(components, "administrative_area_level_1");

  if (!countryPart || !cityPart) return null;

  const countryCode = countryPart.short_name.toUpperCase();
  // Look up by ISO code, not name: Google says "United Kingdom" where the
  // list says "United Kingdom of Great Britain and Northern Ireland".
  const extra = country.findByIso2(countryCode) as any;

  const city = cityPart.long_name;
  const state = statePart?.long_name ?? "";

  return {
    city,
    state,
    country: countryPart.long_name,
    country_code: countryCode,
    region: extra?.region ?? "",
    continent: extra?.continent ?? "",
    currency: {
      curency_code: extra?.currency?.code,
      curency_symbol: extra?.currency?.symbol,
      dialing_code: extra?.dialing_code,
      country_iso_2: extra?.code?.iso2 ?? countryCode,
      country_iso_3: extra?.code?.iso3,
    },
    label: [city, state, countryPart.long_name].filter(Boolean).join(", "),
  };
};

/** The country of any picked place. */
export const extractPlaceCountry = (
  place: google.maps.places.PlaceResult | null | undefined
): PlaceCountry | null => {
  const part = place?.address_components && component(place.address_components, "country");
  if (!part) return null;
  return { country: part.long_name, country_code: part.short_name.toUpperCase() };
};

/** Display string for a picked place: "12 Aba Road, Port Harcourt, Rivers, Nigeria" or "Port Harcourt, Rivers, Nigeria". */
export const getPlaceCompleteName = (
  selected: google.maps.places.PlaceResult | null | undefined,
  place?: google.maps.places.PlaceResult | null
) => {
  const target = selected ?? place;
  const components = target?.address_components;
  if (!components?.length) return "";

  const street = [
    component(components, "street_number")?.long_name,
    component(components, "route")?.long_name,
  ].filter(Boolean).join(" ");
  const city = extractPlaceLocation(target);
  if (city) return [street, city.label].filter(Boolean).join(", ");
  // A bare country pick, or a place with no city component.
  return [street, component(components, "administrative_area_level_1")?.long_name, component(components, "country")?.long_name]
    .filter(Boolean).join(", ");
};

const TYPES_BY_KIND: Record<PlaceKind, string[] | undefined> = {
  address: undefined,
  city: ["(cities)"],
  country: ["(regions)"],
};

const PlacesAutoCompleteInput = ({
  id,
  placeholder,
  kind = "address",
  selectHandler,
  onClear,
  country: countryProp,
  defaultText,
}: Props) => {
  const { place, isLoaded } = useGoogleMapsContext();
  const [selected, setSelected] =
    useState<google.maps.places.PlaceResult | null>(null);
  const { state, toggle } = useToggleState();
  const [recentPlaceSearch, setRecentPlaceSearch] = useLocalStorage<
    google.maps.places.PlaceResult[]
  >(`recent-place-search-${kind}`, []);
  const ref = useRef<any>();

  /* No country restriction unless a caller asks for one. City search used
     to be pinned to "ng" and address search to whatever country the IP
     lookup guessed, so a client hiring outside Nigeria -- or simply
     travelling -- could not find their own city. Google ranks by relevance
     and proximity anyway, so local places still come up first. */
  const restriction = countryProp !== undefined ? countryProp : null;

  const setInputText = (text: string) => {
    const inputEl = document.getElementById(id) as HTMLInputElement | null;
    if (inputEl) inputEl.value = text;
  };

  const closeList = () => {
    // Nothing picked: whatever was typed is not a location, so drop it.
    if (!selected) setInputText(defaultText ?? "");
    toggle();
  };

  useOnClickOutside(ref as RefObject<HTMLElement>, closeList);

  const {
    init,
    setValue,
    suggestions: { data, loading },
  } = usePlacesAutocomplete({
    initOnMount: false,
    cache: 24 * 60 * 60,
    requestOptions: {
      ...(TYPES_BY_KIND[kind] && { types: TYPES_BY_KIND[kind] }),
      ...(restriction && {
        componentRestrictions: { country: restriction.toLowerCase() },
      }),
    },
    debounce: 500,
  });

  const suggestions =
    kind === "country" ? data.filter((p) => p.types.includes("country")) : data;

  const updateSearch = (result: google.maps.places.PlaceResult) => {
    setSelected(result);
    selectHandler && selectHandler(result);
    toggle();
    const currentSearch = getPlaceCompleteName(result);
    const seen = recentPlaceSearch
      .slice(0, 2)
      .some((r) => getPlaceCompleteName(r) === currentSearch);
    if (seen) return;
    setRecentPlaceSearch((prev) => [result, prev[0]].filter(Boolean));
  };

  const handleSelecion = async (
    selection: google.maps.places.AutocompletePrediction
  ) => {
    const result = (await getDetails({
      placeId: selection.place_id,
      fields: ["address_components"],
    })) as google.maps.places.PlaceResult;

    updateSearch(result);
  };

  const handleTyping = (text: string) => {
    setValue(text);
    // Editing after a pick invalidates the pick.
    if (selected) {
      setSelected(null);
      onClear && onClear();
    }
  };

  useEffect(() => {
    setInputText(getPlaceCompleteName(selected, place) || defaultText || "");
  }, [selected, place, defaultText]);

  // Re-init whenever Google Maps loads OR the restriction changes
  useEffect(() => {
    if (isLoaded) init();
  }, [isLoaded, restriction]);

  const emptyHint =
    kind === "country" ? "Type a country and pick it from the list."
    : kind === "city" ? "Type a city and pick it from the list."
    : "Enter your address.";

  return (
    <div className="relative w-full">
      <Input
        onClick={toggle}
        className="input-outline-none size-full border-none px-1 py-0 text-base text-shade-dark placeholder:text-sm"
        type="text"
        onChange={(e) => handleTyping(e.target.value)}
        placeholder={placeholder}
        name="places-web-search"
        id={id}
        disabled={!isLoaded}
        autoComplete="off"
        showError={false}
      />
      {state ? (
        <section
          ref={ref}
          className="absolute text-muted-foreground top-10 z-10 flex h-60 w-72 md:w-96 flex-col overflow-y-auto rounded-md border bg-white"
        >
          {suggestions.length ? (
            <span className="generic-scrollbar flex h-full w-full flex-1 flex-col space-y-1 p-3">
              {suggestions.map((option, i) => (
                <div
                  key={i}
                  className="flex flex-row items-center space-x-2 py-1"
                >
                  <MdLocationPin className="size-4 text-blue-600" />
                  <p
                    onClick={() => handleSelecion(option)}
                    className="nav-link w-[80%] truncate font-semibold hover:cursor-pointer hover:text-brand-orange"
                  >
                    {option.description}
                  </p>
                </div>
              ))}
            </span>
          ) : (
            <Fragment>
              {recentPlaceSearch.length !== 0 ? (
                <span className="generic-scrollbar flex h-full w-full flex-1 flex-col space-y-1 p-3">
                  <p className="pb-2 !font-semibold">Recent search</p>
                  {recentPlaceSearch.map((option, i) => (
                    <div
                      key={i}
                      className="flex flex-row items-center space-x-2 py-1"
                    >
                      <MdLocationPin className="size-4 text-blue-600" />
                      <p
                        onClick={() => updateSearch(option)}
                        className="nav-link w-[80%] truncate font-semibold hover:cursor-pointer hover:text-brand-orange"
                      >
                        {getPlaceCompleteName(option)}
                      </p>
                    </div>
                  ))}
                </span>
              ) : (
                <div className="flex h-full w-72 flex-col items-center justify-center">
                  <MdLocationPin className="size-10 text-blue-600" />
                  <p className="px-5 text-center">{loading ? "Searching…" : emptyHint}</p>
                </div>
              )}
            </Fragment>
          )}

          <div className="relative flex h-7 w-full justify-end p-2">
            <img
              src={"./powered_by_google.png"}
              alt="powered by google"
              width={100}
              height={50}
            />
          </div>
        </section>
      ) : null}
    </div>
  );
};

export default PlacesAutoCompleteInput;
